/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import type { LearnerProfile_WorkHistory as WorkHistory } from '__generated__/graphql-types-do-not-use';
import { isEqual } from 'lodash';

import {
  Button,
  Dialog,
  Divider,
  InlineNotification,
  StandaloneCheckbox,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import { TrashIcon } from '@coursera/cds-icons';

import { getOccupationIdOrName } from 'bundles/account-profile/common/utils';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ModalDateSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/ModalDateSection';
import EmployerDropdown from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-history/EmployerDropdown';
import type { ValidationErrors } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/validationUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import SelectWithCreateNewOption from 'bundles/employability-common/components/SelectWithCreateNewOption';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const MODAL_FIELD_NAMES = {
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  DESCRIPTION: 'description',
  EMPLOYER: 'employer',
  JOB_TITLE: 'jobTitle',
  SAVE: 'save',
} as const;

const styles = {
  comboBoxLabel: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  withBottomMargin: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  withTopMargin: css`
    margin-top: var(--cds-spacing-300);
  `,
  removeButton: css`
    margin-left: var(--cds-spacing-300);
  `,
  actionButtons: css`
    > div {
      display: inline; /* TrackedButton adds a wrapping div which is unstyled and puts the action buttons on separate lines */
    }
  `,
  removeInheritedStyles: css`
    label {
      margin-bottom: 0;
    }
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedWorkHistoryItem?: WorkHistory;
  refreshDataFlag?: boolean;
};

type RequiredFields = 'save' | 'startDate' | 'endDate' | 'description';
type FormFieldRefs = Record<RequiredFields, MutableRefObject<HTMLDivElement | null>>;

const E2E_SELECTORS = {
  INSTITUTION: 'profile-work-institution',
  ROLE: 'profile-work-role',
  START: 'profile-work-start',
  END: 'profile-work-end',
  CHECKBOX: 'profile-work-current',
  DESCRIPTION: 'profile-work-description',
  SAVE: 'profile-work-save',
} as const;

const EditWorkHistoryModal = ({ isOpen, onClose, selectedWorkHistoryItem, refreshDataFlag }: Props) => {
  const { idToRolesMap, activelyHiringOccupationOptions, nonActivelyHiringOccupationOptions } =
    useContext(OccupationRolesContext);
  const { updateLearnerProfile, profileState = {} } = useContext(ProfileContext);
  const { workHistory = [] } = profileState ?? {};
  const [occupation, setOccupation] = useState<string | undefined>(undefined);
  const [companyName, setCompanyName] = useState<string | undefined>(undefined);
  const [startMonth, setStartMonth] = useState<number | undefined>(undefined);
  const [startYear, setStartYear] = useState<number | undefined>(undefined);
  const [endMonth, setEndMonth] = useState<number | undefined>(undefined);
  const [endYear, setEndYear] = useState<number | undefined>(undefined);
  const [isCurrentlyEmployed, setIsCurrentlyEmployed] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const [errors, setErrors] = useState<{ [field: string]: string | undefined }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const formFieldRefs: FormFieldRefs = {
    startDate: useRef(null),
    endDate: useRef(null),
    description: useRef(null),
    save: useRef(null),
  };

  const { HeadingGroup, Content, Actions } = Dialog;
  useEffect(() => {
    const {
      startDate,
      endDate,
      isCurrentlyEmployed: originalIsCurrentlyEmployed,
      description: originalDescription = '',
      jobTitle,
      employerName,
    } = selectedWorkHistoryItem ?? {};

    setCompanyName(employerName ?? undefined);
    setStartMonth(startDate?.month);
    setStartYear(startDate?.year);
    setEndMonth(endDate?.month);
    setEndYear(endDate?.year);
    setIsCurrentlyEmployed(Boolean(originalIsCurrentlyEmployed));
    setDescription(originalDescription ?? '');

    if (jobTitle) {
      setOccupation(getOccupationIdOrName(jobTitle));
    } else {
      setOccupation(undefined);
    }
  }, [selectedWorkHistoryItem, refreshDataFlag]);

  const scrollToFirstError = (validationErrors: ValidationErrors) => {
    const firstError = Object.keys(validationErrors)[0];
    const firstErrorRef = formFieldRefs[firstError as RequiredFields];
    firstErrorRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  };

  const setStartDateErrors = () => {
    if ((!startMonth && !startYear) || (startMonth && startYear)) {
      return {};
    } else if (!startMonth) {
      return {
        [MODAL_FIELD_NAMES.START_DATE]: _t('Please select a month'),
      };
    } else if (!startYear) {
      return {
        [MODAL_FIELD_NAMES.START_DATE]: _t('Please select a year'),
      };
    }
    return {};
  };

  const setEndDateErrors = () => {
    const formattedStartDate = startMonth && startYear ? new Date(startYear, startMonth - 1) : undefined;
    const formattedEndDate = endMonth && endYear ? new Date(endYear, endMonth - 1) : undefined;

    if (isCurrentlyEmployed && ((endMonth && endYear) || (!endMonth && !endYear))) {
      return {};
    } else if (formattedStartDate && formattedEndDate && formattedEndDate < formattedStartDate) {
      return {
        [MODAL_FIELD_NAMES.END_DATE]: _t('Please select an end date that ends after the start date'),
      };
    } else if (endMonth || endYear) {
      if (!endMonth) {
        return { [MODAL_FIELD_NAMES.END_DATE]: _t('Please select a month') };
      } else if (!endYear) {
        return { [MODAL_FIELD_NAMES.END_DATE]: _t('Please select a year') };
      }
    }
    return {};
  };

  const setDescriptionErrors = () => {
    if (description && description.length > 500) {
      return {
        [MODAL_FIELD_NAMES.DESCRIPTION]: _t('Please enter description that is 500 characters or less'),
      };
    }
    return {};
  };

  const isValidData = () => {
    // at least one field is entered
    if (occupation || companyName || startMonth || startYear || endMonth || endYear || description) {
      const formErrors = { ...setStartDateErrors(), ...setEndDateErrors(), ...setDescriptionErrors() };
      setIsSaving(false);
      setErrors(formErrors);

      const hasErrors = Object.keys(formErrors).length > 0;

      if (hasErrors) {
        scrollToFirstError(formErrors);
      }

      return !hasErrors;
    } else {
      setIsSaving(false);
      setErrors({
        [MODAL_FIELD_NAMES.SAVE]: _t('Please enter at least one field.'),
      });
      scrollToFirstError(errors);

      return false;
    }
  };

  const handleOnClose = () => {
    setErrors({});
    onClose();
  };

  const setupDateFieldChangeHandler =
    (onStateChange: (value: number) => void, resetErrorFieldName: string) => (val: number) => {
      onStateChange(val);

      if (errors[resetErrorFieldName]) {
        setErrors((prevState) => ({ ...prevState, [resetErrorFieldName]: undefined }));
      }
    };

  const onRemove = async () => {
    try {
      const filteredWorkHistory = workHistory.filter((history) => !isEqual(history, selectedWorkHistoryItem));
      await updateLearnerProfile({ workHistory: filteredWorkHistory });
      onClose();
    } catch {
      setIsSaving(false);
      setErrors((prevState) => ({
        ...prevState,
        [MODAL_FIELD_NAMES.SAVE]: _t('Something went wrong. Please try again'),
      }));
      scrollToFirstError(errors);
    }
  };

  const onSave = async () => {
    setIsSaving(true);
    if (isValidData()) {
      const newWorkHistoryItem: WorkHistory = {
        employerName: companyName,
        jobTitle: occupation
          ? {
              ...(idToRolesMap.has(occupation)
                ? { id: occupation, __typename: 'LearnerProfile_OccupationId' }
                : { name: occupation, __typename: 'LearnerProfile_UserGeneratedOccupation' }),
            }
          : undefined,
        description,
        startDate:
          startYear && startMonth
            ? {
                year: startYear,
                month: startMonth,
              }
            : undefined,
        isCurrentlyEmployed,
        ...(isCurrentlyEmployed
          ? {}
          : { endDate: endYear && endMonth ? { year: endYear, month: endMonth } : undefined }),
      };

      try {
        if (!isEqual(newWorkHistoryItem, selectedWorkHistoryItem)) {
          const filteredWorkHistory = workHistory.filter((history) => !isEqual(history, selectedWorkHistoryItem));
          await updateLearnerProfile({ workHistory: [...filteredWorkHistory, newWorkHistoryItem] });
        }

        handleOnClose();
        setIsSaving(false);
      } catch {
        setIsSaving(false);
        setErrors((prevState) => ({
          ...prevState,
          [MODAL_FIELD_NAMES.SAVE]: _t('Something went wrong. Please try again'),
        }));
        scrollToFirstError(errors);
      }
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);

    if (errors[MODAL_FIELD_NAMES.DESCRIPTION]) {
      setDescriptionErrors();
    }
  };

  const alphabetizedActivelyHiringRoles = activelyHiringOccupationOptions.sort((roleA, roleB) =>
    roleA.value.localeCompare(roleB.value)
  );
  const alphabetizedNonActivelyHiringRoles = nonActivelyHiringOccupationOptions.sort((roleA, roleB) =>
    roleA.value.localeCompare(roleB.value)
  );

  return (
    <Dialog open={isOpen} onClose={handleOnClose}>
      <HeadingGroup
        supportText={_t(
          "Add your past work experience. If you're just starting out, you can add internships or volunteer experience instead."
        )}
      >
        {_t('Work experience')}
      </HeadingGroup>
      <Content>
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_work_history_dialog"
          requireFullyVisible={false}
        >
          <Typography2 component="h3" variant="subtitleMedium" css={styles.comboBoxLabel}>
            {_t('Name of institution')}
          </Typography2>

          <EmployerDropdown
            onSelect={(company: string) => setCompanyName(company)}
            placeholder={_t('Select an institution')}
            label={_t('Name of institution')}
            data-e2e={E2E_SELECTORS.INSTITUTION}
          />

          <Typography2 component="h3" variant="subtitleMedium" css={[styles.comboBoxLabel, styles.withTopMargin]}>
            {_t('Role/Job title')}
          </Typography2>

          <SelectWithCreateNewOption
            data-e2e={E2E_SELECTORS.ROLE}
            placeholder={_t('Select a job title')}
            label={_t('Role/Job title')}
            options={[...alphabetizedActivelyHiringRoles, ...alphabetizedNonActivelyHiringRoles]}
            onSelect={(val: string) => setOccupation(val)}
          />

          <Divider color="dark" css={[styles.withBottomMargin, styles.withTopMargin]} />
          <ModalDateSection
            data-e2e={E2E_SELECTORS.START}
            ref={formFieldRefs.startDate}
            title={_t('Start date')}
            selectedMonth={startMonth}
            handleMonthChange={setupDateFieldChangeHandler(setStartMonth, MODAL_FIELD_NAMES.START_DATE)}
            selectedYear={startYear}
            handleYearChange={setupDateFieldChangeHandler(setStartYear, MODAL_FIELD_NAMES.START_DATE)}
            validationStatus={errors[MODAL_FIELD_NAMES.START_DATE]}
          />
          <Divider color="dark" css={styles.withBottomMargin} />
          <ModalDateSection
            data-e2e={E2E_SELECTORS.END}
            ref={formFieldRefs.endDate}
            title={_t('End date')}
            selectedMonth={endMonth}
            handleMonthChange={setupDateFieldChangeHandler(setEndMonth, MODAL_FIELD_NAMES.END_DATE)}
            selectedYear={endYear}
            handleYearChange={setupDateFieldChangeHandler(setEndYear, MODAL_FIELD_NAMES.END_DATE)}
            isReadOnly={isCurrentlyEmployed}
            validationStatus={errors[MODAL_FIELD_NAMES.END_DATE]}
          />
          <StandaloneCheckbox
            aria-label={_t('I currently work here')}
            defaultChecked={isCurrentlyEmployed}
            label={_t('I currently work here')}
            css={styles.withBottomMargin}
            data-e2e={E2E_SELECTORS.CHECKBOX}
            onChange={(_, isChecked) => {
              setIsCurrentlyEmployed(isChecked);
              if (isChecked) {
                setEndMonth(undefined);
                setEndYear(undefined);
              }
            }}
            checked={isCurrentlyEmployed}
          />
          <Divider color="dark" css={styles.withBottomMargin} />
          <TextField
            ref={formFieldRefs.description}
            multiline
            data-e2e={E2E_SELECTORS.DESCRIPTION}
            label={_t('Description')}
            supportText={_t('Add a simple description of your responsibilities and achievements in this role.')}
            characterLimit={500}
            fullWidth
            value={description}
            onChange={handleDescriptionChange}
            type="text"
            validationStatus={errors[MODAL_FIELD_NAMES.DESCRIPTION] ? 'error' : undefined}
            validationLabel={errors[MODAL_FIELD_NAMES.DESCRIPTION]}
            placeholder={_t(
              'Include a few brief details about what you did in this role. Try to focus on your most meaningful accomplishments, and use numbers to quantify them where possible.'
            )}
            inputProps={{ required: false }}
            css={styles.removeInheritedStyles}
          />
        </TrackedDiv>
      </Content>
      <Actions>
        {errors[MODAL_FIELD_NAMES.SAVE] && (
          <InlineNotification
            ref={formFieldRefs.save}
            css={[styles.withBottomMargin, styles.withTopMargin]}
            severity="error"
          >
            {errors[MODAL_FIELD_NAMES.SAVE] ?? ''}
          </InlineNotification>
        )}
        <div css={styles.actionButtons}>
          <Button
            variant="primary"
            onClick={onSave}
            loading={isSaving}
            component={TrackedButton}
            data-e2e={E2E_SELECTORS.SAVE}
            // not tracked in eventing v3
            trackingName="profile_save_work_history"
            requireFullyVisible={false}
            withVisibilityTracking={true}
          >
            {_t('Save')}
          </Button>
          {selectedWorkHistoryItem && (
            <Button
              variant="secondary"
              iconPosition="before"
              icon={<TrashIcon />}
              css={styles.removeButton}
              onClick={onRemove}
              component={TrackedButton}
              // not tracked in eventing v3
              trackingName="profile_remove_work_history"
              requireFullyVisible={false}
              withVisibilityTracking
            >
              {_t('Remove')}
            </Button>
          )}
        </div>
      </Actions>
    </Dialog>
  );
};

const ErrorBoundEditWorkHistoryModal = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="EditWorkHistoryModal"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the edit work history modal. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <EditWorkHistoryModal {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundEditWorkHistoryModal;
